import store from './store/index'
import { getOtpNumber } from './api/waiting'
import moment from 'moment'
import otp from 'speakeasy'

// 숫자 콤마찍기
export function numberWidthComma (num, limit = -1) {
  if (limit !== -1) {
    num = String(num)
    num = (num.length > limit) ? num.substr(0, num.length - 1) : num
  }
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

/**
 * @description locale 값을 서버의 multiLanguage 키로 매핑
 * @param {string} locale - 'ko', 'en', 'jp', 'cn_simplified'
 * @returns {string|undefined} - 'en', 'ja', 'zh-CN' or undefined
 */
export function getMultiLanguageKey (locale) {
  const localeMap = {
    jp: 'ja',
    cn_simplified: 'zh-CN',
    en: 'en'
  }
  return localeMap[locale]
}

/**
 * @description 다국어 필드 값 가져오기 (name, description 등)
 * @param {Object} item - multiLanguage 필드를 가진 객체 { [field]: '', multiLanguage: { [field]: { en, ja, 'zh-CN' } } }
 * @param {string} field - 가져올 필드명 ('name', 'description' 등)
 * @param {string} locale - 현재 locale
 * @returns {string} - 다국어 값 또는 기본 값 (없으면 빈 문자열)
 */
export function getMultiLanguageField (item, field, locale) {
  if (!item || !item[field]) return ''

  // 한국어인 경우 기본 값 반환
  if (locale === 'ko') {
    return item[field]
  }

  const multiLanguageKey = getMultiLanguageKey(locale)

  // 다국어 정보가 있고 해당 언어가 존재하는 경우
  if (multiLanguageKey && item.multiLanguage && item.multiLanguage[field] && item.multiLanguage[field][multiLanguageKey]) {
    return item.multiLanguage[field][multiLanguageKey]
  }

  // 기본값 반환
  return item[field]
}

/**
 * @description 다국어 이름 가져오기 (queue, request, personnelType 등)
 * @param {Object} item - queue 또는 request 객체
 * @param {string} locale - 현재 locale
 * @returns {string} - 다국어 이름 또는 기본 이름
 */
export function getMultiLanguageName (item, locale) {
  return getMultiLanguageField(item, 'name', locale)
}

// TB-14763: 인원 유형 사용 매장 여부 (설정 on + 유형 1개 이상)
// 설정만 켜져 있고 유형이 비어 있으면 기존 성인/유아 UI로 동작한다
export function isUsePersonnelTypes (restaurant) {
  return Boolean(restaurant && restaurant.usePersonnelTypes && Array.isArray(restaurant.personnelTypes) && restaurant.personnelTypes.length > 0)
}

/**
 * @description 인원 유형별 선택 인원에서 서버 요청용 인원 합계 계산
 * - numOfPeople: 총인원에 포함되는 유형(isChild=false) 합계
 * - childCount: 총인원에 포함되지 않는 유형(isChild=true) 합계
 * @param {Array<{ isChild: boolean, count: number }>} personnelTypes
 * @returns {{ numOfPeople: number, childCount: number }}
 */
export function sumPersonnelCounts (personnelTypes) {
  const result = { numOfPeople: 0, childCount: 0 }
  if (!Array.isArray(personnelTypes)) return result
  personnelTypes.forEach(({ isChild, count }) => {
    const num = Number(count) || 0
    if (isChild) {
      result.childCount += num
    } else {
      result.numOfPeople += num
    }
  })
  return result
}

// OTP 갱신 step(초). 호출 스크린(WD)·서버와 일치해야 같은 코드가 표시된다.
const OTP_STEP_SECONDS = 3600
const OTP_POLL_INTERVAL_MS = 1000

// 대기 확정 코드 생성
export function exce (idx) {
  const secret = `Tabling!!_${idx}`
  return otp.totp({ secret, time: moment().unix(), step: OTP_STEP_SECONDS, algorithm: 'sha256' })
}

export function renewalOtpNumber () {
  clearInterval(store.state.timeoutObj)
  let lastStep = null

  // 매 tick마다 현재 step 구간을 비교해 구간이 바뀐 경우에만 재생성한다.
  // 한 번 실패해도 다음 tick에서 재시도되므로 갱신 체인이 끊기지 않는다.
  const tick = async () => {
    const step = Math.floor(moment().unix() / OTP_STEP_SECONDS)
    if (step === lastStep) {
      return
    }
    try { // OTP 로컬에서 생성
      const localOtp = exce(store.state.restaurant.idx)
      store.commit('otpNumber', localOtp)
      lastStep = step
      console.debug('renewalOtpNumber---', localOtp, new Date())
    } catch (error) { // 로컬 생성 실패시 서버요청으로 폴백
      if (!store.getters.getAccessToken) {
        console.error('renewalOtpNumber local error---', error)
        return
      }
      try {
        const { data } = await getOtpNumber(store.getters.getStoreIdx)
        store.commit('otpNumber', data.token)
        lastStep = step
        console.debug('renewalOtpNumber(server)---', data.token, new Date())
      } catch (serverError) {
        console.error('renewalOtpNumber error---', error, serverError)
      }
    }
  }

  tick()
  store.commit('timeoutObj', setInterval(tick, OTP_POLL_INTERVAL_MS))
}

// 특정 매장은 최대 인원 초과 시 기본 안내 문구(ask_staff) 대신 커스텀 문구를 노출
// TB-14651: 다담석갈비 (다른 번호로 분리 접수 안내, 매장 부착 안내문 문구 그대로 사용)
// 대상 매장이 아니면 null 반환 (기본 i18n 문구 사용)
export function getCustomMaxPersonMsg (storeIdx) {
  const targets = (process.env.VUE_APP_CUSTOM_MAX_PERSON_MSG || '').split(',')
  if (!storeIdx || !targets.includes(storeIdx.toString())) {
    return null
  }
  // 예시 문단은 본문보다 작고 얇게, 경고 문단은 red로 강조한다.
  // AlertPopup의 .alert_msg는 v-html이라 scoped style이 적용되지 않아 inline style을 사용한다.
  const exampleStyle =
    'font-weight:400;font-size:32px;font-size:min(2.5vw, 32px)'
  // .alert_msg는 display:flex(row)라 자식이 가로로 배치되므로
  // 문단이 세로로 쌓이도록 전체를 하나의 컨테이너로 감싼다.
  return (
    '<div>' +
    '<div>4명 이상일 경우 서로 다른 전화번호로 각각 등록해주세요.</div>' +
    `<div style="margin-top:10px;${exampleStyle}">(예) 6명일 경우</div>` +
    `<div style="${exampleStyle}">010-1111-1111 3명 등록 / 010-2222-2222 3명 등록</div>` +
    `<div style="${exampleStyle}">(각각 다른 번호로 등록하셔야 합니다.)</div>` +
    '<div style="margin-top:10px;color:#D81B1B">4명 이상일 경우 같은 테이블에 앉을 수 없습니다.</div>' +
    '</div>'
  )
}

// 특정 매장은 '이름' 입력란 placeholder와 접수 확인 화면 라벨을 커스텀 텍스트로 표기
// TB-14754: 비외식업(병원) 매장, 이름 칸에 생년월일도 함께 입력받기 위해 "이름 / 생년월일" 노출
// 대상 매장이 아니면 null 반환 (기본 i18n 문구 사용)
export function getCustomNameLabel (storeIdx) {
  const targets = (process.env.VUE_APP_CUSTOM_NAME_LABEL || '').split(',')
  if (!storeIdx || !targets.includes(storeIdx.toString())) {
    return null
  }
  return '이름 / 생년월일'
}

/**
 * @description 선택한 메뉴를 매장이 설정한 메뉴 정렬 순서로 정렬
 * TB-14967: 주문 생성 요청 시 고객이 담은 순서가 아닌 매장 설정 순서(priority)로 전송해야
 * 사장님보드/POS에 조리 순서대로 노출된다. 주문 가능 메뉴 조회 API 응답이 이미
 * 카테고리 priority → 메뉴 priority 순으로 내려오므로 그 배열 순서를 기준으로 삼는다.
 * - 같은 메뉴(_id)를 사이즈/옵션만 다르게 여러 건 담은 경우는 담은 순서를 유지한다.
 * - 매장 메뉴 목록에 없는 메뉴는 맨 뒤에 담은 순서대로 둔다.
 * - 매장 메뉴 목록이 없으면 원본 순서를 그대로 반환한다.
 * @param {Array<object>} selectedMenus - 고객이 담은 메뉴 목록 (_id 필수)
 * @param {Array<object>} categoryList - restaurantMenuInfo.list (카테고리별 menus 배열)
 * @returns {Array<object>} 정렬된 새 배열 (원본은 변경하지 않음)
 */
export function sortMenusByRestaurantOrder (selectedMenus, categoryList) {
  if (!Array.isArray(selectedMenus)) return []
  if (!Array.isArray(categoryList) || categoryList.length === 0) return selectedMenus.slice()

  const orderMap = new Map()
  categoryList.forEach((category) => {
    const menus = (category && Array.isArray(category.menus)) ? category.menus : []
    menus.forEach((menu) => {
      if (menu && menu._id != null && !orderMap.has(menu._id)) {
        orderMap.set(menu._id, orderMap.size)
      }
    })
  })

  const getOrder = (menu) => {
    const order = orderMap.get(menu && menu._id)
    return (order === undefined) ? Number.MAX_SAFE_INTEGER : order
  }

  // 구형 Chrome(V8 7.0 미만)에서는 Array#sort가 안정 정렬이 아니므로
  // 원본 인덱스를 2차 정렬 키로 사용해 담은 순서를 보장한다.
  return selectedMenus
    .map((menu, index) => ({ menu, index, order: getOrder(menu) }))
    .sort((a, b) => (a.order - b.order) || (a.index - b.index))
    .map(({ menu }) => menu)
}
